import { HttpMethods, makeRequest } from '../helpers/http.helper';
import type { ApprovalOrderRow, ApprovalOrdersPage, OrderApprovalStatus } from '../types/order-approval.type';

interface GenericResponse<T> {
  result: T;
}

/** Every route-run collection (done and cancelled), optionally for one day. */
export async function listCollectionOrders(
  organizationId: string,
  runDate?: string
): Promise<ApprovalOrdersPage> {
  const response = (await makeRequest({
    url: '/pickup_order/ops/collections',
    method: HttpMethods.GET,
    params: { organization_id: organizationId, ...(runDate ? { run_date: runDate } : {}) },
  })) as GenericResponse<ApprovalOrdersPage>;
  return response.result;
}

/**
 * Orders delivered into this organization's facilities, by verification state (pending
 * by default), optionally for one day. Includes loads other organizations delivered
 * through a linked facility.
 */
export async function listPendingApprovalOrders(
  organizationId: string,
  options: { approvalStatus?: OrderApprovalStatus; runDate?: string } = {}
): Promise<ApprovalOrdersPage> {
  const response = (await makeRequest({
    url: '/pickup_order/ops/pending-approval',
    method: HttpMethods.GET,
    params: {
      organization_id: organizationId,
      approval_status: options.approvalStatus ?? 'pending',
      ...(options.runDate ? { run_date: options.runDate } : {}),
    },
  })) as GenericResponse<ApprovalOrdersPage>;
  return response.result;
}

/**
 * Facility manager verifies the quantity. A corrected weight is honoured only while
 * the collection point has not confirmed it; flagged orders are repriced from the agreement.
 */
export async function approvePickupOrder(
  organizationId: string,
  pickupOrderId: string,
  correctedWeightKg?: number
): Promise<ApprovalOrderRow> {
  const response = (await makeRequest({
    url: `/pickup_order/ops/${pickupOrderId}/approve`,
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: correctedWeightKg != null ? { corrected_weight_kg: correctedWeightKg } : {},
  })) as GenericResponse<ApprovalOrderRow>;
  return response.result;
}

/**
 * Facility manager refuses a delivered order with a justification. The order's sale
 * drops to zero (the invoice keeps its weighed quantity) and the delivering organization
 * is told the reason.
 */
export async function rejectPickupOrder(
  organizationId: string,
  pickupOrderId: string,
  reason: string
): Promise<ApprovalOrderRow> {
  const response = (await makeRequest({
    url: `/pickup_order/ops/${pickupOrderId}/reject`,
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: { reason },
  })) as GenericResponse<ApprovalOrderRow>;
  return response.result;
}

/**
 * Undo an accept or reject so the order returns to the queue.
 *
 * Platform admins only, and absent from the integration API: a partner must not be
 * able to reverse its own refusal, or "final" would mean nothing. The original price
 * cannot be restored -- rejecting overwrites it -- so the server re-resolves it from
 * the supplier agreement, and the order returns flagged if none is found.
 */
export async function reinstateOrderDecision(
  pickupOrderId: string,
  reason?: string
): Promise<{ pickup_order_id: string; approval_status: string; sale_amount: number }> {
  const response = (await makeRequest({
    url: `/pickup_order/admin/${pickupOrderId}/reinstate-decision`,
    method: HttpMethods.POST,
    data: { reason: reason?.trim() || null },
  })) as GenericResponse<{ pickup_order_id: string; approval_status: string; sale_amount: number }>;
  return response.result;
}
